import { useState, useEffect, useCallback } from 'react';
import { cashflowApi, formatCurrency } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, ReferenceLine
} from 'recharts';
import { Calendar, TrendingUp, TrendingDown, PoundSterling } from 'lucide-react';
import { toast } from 'sonner';
import PropertyFilter from './PropertyFilter';

const RANGE_OPTIONS = [
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
  { value: 'custom', label: 'Custom' },
];

export const CashflowWidget = ({ selectedPropertyIds = [], onPropertySelectionChange }) => {
  const [loading, setLoading] = useState(true);
  const [rangeType, setRangeType] = useState('year');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [mode, setMode] = useState('actual');
  const [data, setData] = useState(null);
  const [visibleSeries, setVisibleSeries] = useState({
    income: true,
    expenses: true,
    net: true,
  });

  const fetchCashflow = useCallback(async () => {
    if (selectedPropertyIds.length === 0) return;
    
    setLoading(true);
    try {
      const response = await cashflowApi.get(
        rangeType,
        rangeType === 'custom' ? customStart : undefined,
        rangeType === 'custom' ? customEnd : undefined,
        undefined,
        selectedPropertyIds,
        mode
      );
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load cashflow data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [rangeType, customStart, customEnd, selectedPropertyIds, mode]);

  useEffect(() => {
    if (rangeType !== 'custom' || (customStart && customEnd)) {
      fetchCashflow();
    }
  }, [rangeType, selectedPropertyIds, mode, fetchCashflow]);

  const handleRangeChange = (value) => {
    setRangeType(value);
    if (value !== 'custom') {
      setCustomStart('');
      setCustomEnd('');
    }
  };

  const handleCustomDateApply = () => {
    if (customStart && customEnd) {
      fetchCashflow();
    } else {
      toast.error('Please select both start and end dates');
    }
  };

  const toggleSeries = (series) => {
    setVisibleSeries(prev => ({
      ...prev,
      [series]: !prev[series]
    }));
  };

  // Transform data for chart with net calculation
  const chartData = data?.monthlyData?.map(item => ({
    ...item,
    net: item.income - item.expenses,
  })) || [];

  // Get source icon
  const getSourceIcon = (source) => {
    switch (source) {
      case 'pdf': return 'PDF';
      case 'manual': return 'Manual';
      case 'mixed': return 'PDF+Manual';
      case 'estimate': return 'Estimated';
      case 'forecast': return 'Forecast';
      default: return '';
    }
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    const dataPoint = chartData.find(d => d.month === label);
    const income = dataPoint?.income || 0;
    const expenses = dataPoint?.expenses || 0;
    const net = income - expenses;
    const source = dataPoint?.source;

    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-4 min-w-[200px]">
        <p className="font-semibold text-slate-900 mb-2 pb-2 border-b border-slate-100">
          {label} {data?.dateRange?.start ? new Date(data.dateRange.start).getFullYear() : new Date().getFullYear()}
        </p>
        <div className="space-y-1.5">
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-600">Income</span>
            <span className="text-sm font-semibold text-emerald-600">
              {formatCurrency(income)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-600">Expenses</span>
            <span className="text-sm font-semibold text-red-500">
              {formatCurrency(expenses)}
            </span>
          </div>
          <div className="flex justify-between items-center pt-1.5 border-t border-slate-100">
            <span className="text-sm text-slate-600">Net</span>
            <span className={`text-sm font-semibold ${net >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
              {formatCurrency(net)}
            </span>
          </div>
          {source && (
            <div className="flex justify-between items-center pt-1.5 border-t border-slate-100">
              <span className="text-xs text-slate-400">Source</span>
              <span className="text-xs text-slate-500">
                {getSourceIcon(source)}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Custom legend
  const renderLegend = () => (
    <div className="flex items-center gap-3 justify-center mt-4">
      <button
        onClick={() => toggleSeries('income')}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
          visibleSeries.income 
            ? 'bg-emerald-100 text-emerald-700' 
            : 'bg-slate-100 text-slate-400 line-through'
        }`}
        data-testid="legend-income"
      >
        <span className={`w-3 h-3 rounded-full ${visibleSeries.income ? 'bg-emerald-500' : 'bg-slate-300'}`} />
        Income
      </button>
      <button
        onClick={() => toggleSeries('expenses')}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
          visibleSeries.expenses 
            ? 'bg-red-100 text-red-700' 
            : 'bg-slate-100 text-slate-400 line-through'
        }`}
        data-testid="legend-expenses"
      >
        <span className={`w-3 h-3 rounded-full ${visibleSeries.expenses ? 'bg-red-400' : 'bg-slate-300'}`} />
        Expenses
      </button>
      <button
        onClick={() => toggleSeries('net')}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
          visibleSeries.net 
            ? 'bg-blue-100 text-blue-700' 
            : 'bg-slate-100 text-slate-400 line-through'
        }`}
        data-testid="legend-net"
      >
        <span className={`w-3 h-3 rounded-full ${visibleSeries.net ? 'bg-blue-500' : 'bg-slate-300'}`} />
        Net
      </button>
    </div>
  );

  const formatYAxis = (value) => {
    if (Math.abs(value) >= 1000) {
      return `£${(value / 1000).toFixed(0)}k`;
    }
    return `£${value}`;
  };

  // Check if showing only net (hide income+expenses bars)
  const showOnlyNet = visibleSeries.net && !visibleSeries.income && !visibleSeries.expenses;

  return (
    <Card className="border-slate-200" data-testid="cashflow-widget">
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <CardTitle className="text-lg font-semibold font-display flex items-center gap-2">
              <PoundSterling className="w-5 h-5 text-slate-500" />
              Cashflow
            </CardTitle>
            
            {/* Actual / Forecast Toggle */}
            <div className="flex bg-slate-100 rounded-lg p-0.5" data-testid="cashflow-mode-toggle">
              <button
                onClick={() => setMode('actual')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  mode === 'actual'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="cashflow-mode-actual"
              >
                Actual
              </button>
              <button
                onClick={() => setMode('forecast')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  mode === 'forecast'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="cashflow-mode-forecast"
              >
                Forecast
              </button>
            </div>
          </div>
          
          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Property Filter */}
            <PropertyFilter
              selectedIds={selectedPropertyIds}
              onSelectionChange={onPropertySelectionChange}
            />

            {/* Date Range Selector */}
            <Select value={rangeType} onValueChange={handleRangeChange}>
              <SelectTrigger className="w-32" data-testid="cashflow-range-selector">
                <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RANGE_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {rangeType === 'custom' && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="custom-date-btn">
                    Select Dates
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="start-date">Start Date</Label>
                      <Input
                        id="start-date"
                        type="date"
                        value={customStart}
                        onChange={(e) => setCustomStart(e.target.value)}
                        data-testid="custom-start-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="end-date">End Date</Label>
                      <Input
                        id="end-date"
                        type="date"
                        value={customEnd}
                        onChange={(e) => setCustomEnd(e.target.value)}
                        data-testid="custom-end-date"
                      />
                    </div>
                    <Button 
                      onClick={handleCustomDateApply} 
                      className="w-full bg-slate-900 hover:bg-slate-800"
                      data-testid="apply-custom-dates"
                    >
                      Apply
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>

        {/* Mode indicator */}
        {mode === 'forecast' && (
          <p className="text-xs text-amber-600 bg-amber-50 px-3 py-1.5 rounded-md mt-2 inline-block">
            Showing projected values based on your defined monthly rent and expenses
          </p>
        )}
      </CardHeader>

      <CardContent>
        {/* Summary Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="cashflow-summary">
          <div className="bg-emerald-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
              <span className="text-sm text-emerald-700">Total Income</span>
            </div>
            <p className="text-2xl font-bold text-emerald-700" data-testid="total-income">
              {formatCurrency(data?.totalIncome || 0)}
            </p>
          </div>

          <div className="bg-red-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown className="w-4 h-4 text-red-600" />
              <span className="text-sm text-red-700">Total Expenses</span>
            </div>
            <p className="text-2xl font-bold text-red-600" data-testid="total-expenses-cashflow">
              {formatCurrency(data?.totalExpenses || 0)}
            </p>
          </div>

          <div className={`rounded-lg p-4 ${(data?.netCashflow || 0) >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
            <div className="flex items-center gap-2 mb-1">
              <PoundSterling className={`w-4 h-4 ${(data?.netCashflow || 0) >= 0 ? 'text-blue-600' : 'text-orange-600'}`} />
              <span className={`text-sm ${(data?.netCashflow || 0) >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                Net Cashflow
              </span>
            </div>
            <p className={`text-2xl font-bold ${(data?.netCashflow || 0) >= 0 ? 'text-blue-700' : 'text-orange-700'}`} data-testid="net-cashflow">
              {formatCurrency(data?.netCashflow || 0)}
            </p>
          </div>
        </div>

        {/* Chart */}
        <div className="h-72" data-testid="cashflow-chart">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="spinner w-8 h-8"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={chartData} 
                margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                barCategoryGap="20%"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  tickFormatter={formatYAxis}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <ReferenceLine y={0} stroke="#e2e8f0" />
                {visibleSeries.income && !showOnlyNet && (
                  <Bar 
                    dataKey="income" 
                    fill="#10B981" 
                    radius={[4, 4, 0, 0]} 
                    name="Income"
                  />
                )}
                {visibleSeries.expenses && !showOnlyNet && (
                  <Bar 
                    dataKey="expenses" 
                    fill="#EF4444" 
                    radius={[4, 4, 0, 0]} 
                    name="Expenses"
                  />
                )}
                {visibleSeries.net && (
                  <Bar 
                    dataKey="net" 
                    fill="#3B82F6" 
                    radius={[4, 4, 0, 0]} 
                    name="Net"
                  />
                )}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Custom Legend */}
        {renderLegend()}

        {/* Date Range Info */}
        {data?.dateRange && (
          <p className="text-xs text-slate-400 text-center mt-3">
            Showing data from {new Date(data.dateRange.start).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })} to {new Date(data.dateRange.end).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default CashflowWidget;
